<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card-title>
          <v-row>
            <v-col class="col-12 col-md-8">
              <h1 class="primary-color">Interview Analysis Config</h1>
            </v-col>
            <v-spacer />
            <v-col class="col-12 col-md-3">
              <v-btn class="new-item-btn float-right" block @click="openCreate">
                <v-icon size="20">mdi-plus</v-icon>&nbsp;&nbsp;Create config
              </v-btn>
            </v-col>
          </v-row>
        </v-card-title>
        <v-alert v-if="message" outlined dismissible :color="success ? 'success' : 'error'">
          {{ message }}
        </v-alert>
        <v-data-table
          :headers="headers"
          :items="configs"
          :loading="loading"
          item-key="id"
          :server-items-length="pagination.totalItems"
          :items-per-page="pagination.itemsPerPage"
          :page.sync="pagination.page"
          @update:page="handlePageChange"
        >
          <template #[`item.createdAt`]="{ item }">
            {{ item.createdAt | formatDate }}
          </template>
          <template #[`item.configVersion`]="{ item }">
            <v-chip
              v-if="configVersionOf(item) !== null"
              x-small
              outlined
              color="primary"
              class="iac-version-chip"
            >
              Config v{{ configVersionOf(item) }}
            </v-chip>
            <span v-else class="grey--text">—</span>
          </template>
          <template #[`item.actions`]="{ item }">
            <v-btn icon @click="openEdit(item)">
              <v-icon size="20" color="primary">mdi-square-edit-outline</v-icon>
            </v-btn>
            <v-btn icon @click="remove(item)">
              <v-icon size="20" color="error">mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <v-dialog
      v-model="dialog"
      fullscreen
      persistent
      transition="dialog-bottom-transition"
      content-class="interview-config-dialog-content"
    >
      <v-card class="interview-config-dialog-card d-flex flex-column">
        <v-toolbar flat dense class="flex-shrink-0 interview-config-dialog-toolbar">
          <v-toolbar-title class="primary-color text-h6">
            {{ editing ? 'Update' : 'Create' }} Interview Analysis Config
          </v-toolbar-title>
          <v-chip
            v-if="editing && currentConfigVersion !== null"
            small
            outlined
            color="primary"
            class="ml-3 iac-version-chip"
          >
            Config v{{ currentConfigVersion }}
          </v-chip>
          <v-spacer />
          <v-btn icon type="button" aria-label="Close" @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-form
          ref="configForm"
          class="interview-config-dialog-form d-flex flex-column flex-grow-1"
          style="min-height: 0"
          @submit.prevent="save"
        >
          <v-card-text class="interview-config-dialog-body flex-grow-1 pa-0">
            <div class="iac-layout">
              <nav class="iac-nav">
                <p class="iac-nav__group">Configuration</p>
                <button
                  v-for="s in navSections"
                  :key="s.key"
                  type="button"
                  class="iac-nav__item"
                  :class="{ 'iac-nav__item--active primary--text': activeSection === s.key }"
                  @click="activeSection = s.key"
                >
                  <span class="iac-nav__item-main">
                    <v-icon size="18" class="iac-nav__item-icon">{{ s.icon }}</v-icon>
                    <span>{{ s.label }}</span>
                  </span>
                  <span v-if="s.badge" class="iac-nav__item-badge">{{ s.badge }}</span>
                </button>
              </nav>

              <div class="iac-content">
                <!-- ===================== GENERAL ===================== -->
                <section v-show="activeSection === 'general'" class="iac-section">
                  <header class="iac-section__head">
                    <h2 class="iac-section__title">General</h2>
                    <p class="iac-section__subtitle">
                      Name this configuration so you can find it later.
                    </p>
                  </header>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.name"
                        label="Name *"
                        :rules="rules"
                        dense
                        outlined
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field v-model="form.description" label="Description" dense outlined />
                    </v-col>
                  </v-row>
                  <div class="iac-other-modules mt-2">
                    <p class="iac-group__title mb-1">Other analysis modules</p>
                    <p class="caption grey--text mb-3">Not yet configurable — coming soon.</p>
                    <div class="d-flex flex-wrap" style="gap: 12px">
                      <v-card outlined class="iac-disabled-chip">
                        <v-icon size="18" class="mr-2">mdi-cube-outline</v-icon>Triple A Toolkit
                      </v-card>
                      <v-card outlined class="iac-disabled-chip">
                        <v-icon size="18" class="mr-2">mdi-cube-outline</v-icon>Pre Competencial
                      </v-card>
                      <v-card outlined class="iac-disabled-chip">
                        <v-icon size="18" class="mr-2">mdi-cube-outline</v-icon>Cultural Fit
                      </v-card>
                    </div>
                  </div>
                </section>

                <!-- ===================== GESTURES ===================== -->
                <section v-show="activeSection === 'gestures'" class="iac-section">
                  <header class="iac-section__head">
                    <h2 class="iac-section__title">Gestures</h2>
                    <p class="iac-section__subtitle">
                      What we observe in body language, and how strictly we detect it.
                    </p>
                  </header>

                  <v-card outlined class="iac-subcard">
                    <v-card-title
                      class="sub-engine-card-title d-flex flex-wrap align-center"
                      style="gap: 8px"
                    >
                      <span>Gesture catalog</span>
                      <v-menu offset-y left>
                        <template #activator="{ on, attrs }">
                          <v-btn icon small v-bind="attrs" v-on="on">
                            <v-icon small color="primary">mdi-information-outline</v-icon>
                          </v-btn>
                        </template>
                        <v-card max-width="380">
                          <v-card-text class="py-2 px-3 caption">
                            <strong>Absolute</strong>: episode count over the whole video is banded
                            rare / intermediate / habitual. Rare and intermediate raise gesture
                            alerts; habitual is the person's tic and is discarded. Bands never
                            subtract coherence by themselves.<br />
                            <strong>Baseline</strong>: frequency-deviation analysis (existing
                            behavior).<br />
                            <strong>Apply sustained rule</strong>: sustained episodes (&ge; gesture
                            threshold ms) are discarded as "Sustained &mdash; not valid" and never
                            analyzed.<br />
                            <strong>Enabled</strong>: when off, the gesture is ignored completely
                            (no episodes, no UI).
                          </v-card-text>
                        </v-card>
                      </v-menu>
                      <v-spacer />
                      <v-btn
                        small
                        outlined
                        color="primary"
                        type="button"
                        :disabled="!gestureCatalog.length"
                        @click="resetGestureSettingsToDefaults"
                      >
                        Reset to defaults
                      </v-btn>
                    </v-card-title>
                    <v-card-text class="sub-engine-card-body">
                      <v-alert
                        v-if="gestureCatalogError"
                        dense
                        outlined
                        type="warning"
                        class="mb-3"
                      >
                        Could not load gesture catalog
                      </v-alert>
                      <v-progress-linear v-if="gestureCatalogLoading" indeterminate class="mb-2" />
                      <template v-if="gestureCatalog.length">
                        <div class="d-flex align-center flex-wrap mb-3" style="gap: 8px">
                          <v-text-field
                            v-model="gestureSearch"
                            prepend-inner-icon="mdi-magnify"
                            placeholder="Search gesture…"
                            dense
                            hide-details
                            outlined
                            clearable
                            class="gesture-search"
                            style="max-width: 280px"
                          />
                          <v-chip small outlined>
                            Enabled:
                            <span class="ml-1 font-weight-medium"
                              >{{ enabledGestureCount }} / {{ gestureCatalog.length }}</span
                            >
                          </v-chip>
                        </div>

                        <v-alert
                          v-if="unmappedGestures.length"
                          dense
                          outlined
                          type="warning"
                          class="mb-3"
                        >
                          <span class="font-weight-medium"
                            >{{ unmappedGestures.length }} of {{ gestureCatalog.length }} gestures
                            have no coherence rule assigned.</span
                          >
                          They are detected and listed in the gestures panel, but they never reach
                          the semantic layer and never affect the coherence score. Marked with
                          <v-icon x-small color="warning">mdi-alert-outline</v-icon> below.
                        </v-alert>
                        <v-alert
                          v-if="partiallyMappedGestures.length"
                          dense
                          outlined
                          type="info"
                          class="mb-3"
                        >
                          {{ partiallyMappedGestures.length }} more are mapped only for some of
                          their variants (marked with
                          <v-icon x-small color="grey">mdi-alert-circle-outline</v-icon>). Hover the
                          icon to see which variants are left out.
                        </v-alert>

                        <div
                          v-for="group in filteredGestureGroups"
                          :key="'gesture-cat-' + group.category"
                          class="gesture-category"
                        >
                          <div
                            class="gesture-category__head d-flex align-center flex-wrap"
                            style="gap: 8px"
                          >
                            <span class="gesture-category__title">{{ group.title }}</span>
                            <v-chip x-small outlined>{{ group.items.length }}</v-chip>
                            <v-spacer />
                            <v-btn
                              x-small
                              outlined
                              color="primary"
                              type="button"
                              @click="setCategoryMethod(group.category, 'absolute')"
                            >
                              All absolute
                            </v-btn>
                            <v-btn
                              x-small
                              outlined
                              color="primary"
                              type="button"
                              @click="setCategoryMethod(group.category, 'baseline')"
                            >
                              All baseline
                            </v-btn>
                          </div>
                          <div class="gesture-category__body">
                            <div
                              v-for="item in group.items"
                              :key="'gesture-row-' + item.gestureName"
                              class="gesture-row d-flex align-center flex-wrap"
                            >
                              <div class="gesture-row__label">
                                <div class="d-flex align-center" style="gap: 4px">
                                  <div class="body-2 text-truncate" :title="item.documentLabelEn">
                                    {{ item.documentLabelEn }}
                                  </div>
                                  <v-tooltip bottom max-width="360">
                                    <template #activator="{ on, attrs }">
                                      <v-icon
                                        v-if="gestureBindingIcon(item)"
                                        v-bind="attrs"
                                        x-small
                                        :color="gestureBindingColor(item)"
                                        v-on="on"
                                      >
                                        {{ gestureBindingIcon(item) }}
                                      </v-icon>
                                    </template>
                                    <span>{{ gestureBindingDetail(item) }}</span>
                                  </v-tooltip>
                                </div>
                                <div
                                  class="caption grey--text gesture-row__name text-truncate"
                                  :title="item.gestureName"
                                >
                                  {{ item.gestureName }}
                                </div>
                              </div>
                              <div
                                v-if="gestureSettings[item.gestureName]"
                                class="gesture-row__controls d-flex align-center flex-wrap"
                              >
                                <v-btn-toggle
                                  v-model="gestureSettings[item.gestureName].method"
                                  mandatory
                                  dense
                                >
                                  <v-btn
                                    small
                                    value="absolute"
                                    type="button"
                                    :disabled="!gestureSettings[item.gestureName].enabled"
                                  >
                                    Absolute
                                  </v-btn>
                                  <v-btn
                                    small
                                    value="baseline"
                                    type="button"
                                    :disabled="!gestureSettings[item.gestureName].enabled"
                                  >
                                    Baseline
                                  </v-btn>
                                </v-btn-toggle>
                                <v-switch
                                  v-model="gestureSettings[item.gestureName].applySustainedRule"
                                  dense
                                  hide-details
                                  class="mt-0 pt-0"
                                  label="Apply sustained rule"
                                  :disabled="!gestureSettings[item.gestureName].enabled"
                                />
                                <v-switch
                                  v-model="gestureSettings[item.gestureName].discardWhileSpeaking"
                                  dense
                                  hide-details
                                  class="mt-0 pt-0"
                                  label="Discard while speaking"
                                  :disabled="!gestureSettings[item.gestureName].enabled"
                                />
                                <v-switch
                                  v-model="gestureSettings[item.gestureName].enabled"
                                  dense
                                  hide-details
                                  class="mt-0 pt-0"
                                  label="Enabled"
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        <v-card v-if="!filteredGestureGroups.length" flat class="pa-6 text-center">
                          <v-icon size="36" color="grey">mdi-magnify-close</v-icon>
                          <p class="mt-2 mb-0 grey--text text--darken-1">
                            No gestures match the current search.
                          </p>
                        </v-card>
                      </template>
                    </v-card-text>
                  </v-card>

                  <v-card outlined class="iac-subcard">
                    <v-card-title class="sub-engine-card-title">Detection sensitivity</v-card-title>
                    <v-card-text class="sub-engine-card-body">
                      <IacSliderField
                        v-for="f in gestureSensitivityFields"
                        :key="f.key"
                        :label="f.label"
                        :help="f.help"
                        :min="f.min"
                        :max="f.max"
                        :step="f.step"
                        :unit="f.unit"
                        :decimals="f.decimals"
                        :value="form.analysisEngines[f.engine][f.key]"
                        @input="setField(f, $event)"
                      />
                    </v-card-text>
                  </v-card>

                  <v-card outlined class="iac-subcard">
                    <v-card-title class="sub-engine-card-title">Frequency bands</v-card-title>
                    <v-card-text class="sub-engine-card-body">
                      <v-row>
                        <v-col cols="12" sm="6">
                          <v-text-field
                            v-model.number="
                              form.analysisEngines.gestures.absoluteBands.rareMaxCount
                            "
                            type="number"
                            min="0"
                            :rules="rareMaxCountRules"
                            dense
                          >
                            <template #label>
                              <span>Rare up to</span>
                              <v-menu offset-y left>
                                <template #activator="{ on, attrs }">
                                  <v-btn icon small v-bind="attrs" v-on="on">
                                    <v-icon small color="primary">mdi-information-outline</v-icon>
                                  </v-btn>
                                </template>
                                <v-card max-width="320">
                                  <v-card-text class="py-2 px-3 caption">
                                    Gesture appears this many times or fewer &rarr; rare (strong
                                    signal).
                                  </v-card-text>
                                </v-card>
                              </v-menu>
                            </template>
                          </v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6">
                          <v-text-field
                            v-model.number="
                              form.analysisEngines.gestures.absoluteBands.intermediateMaxCount
                            "
                            type="number"
                            min="1"
                            :rules="intermediateMaxCountRules"
                            dense
                          >
                            <template #label>
                              <span>Intermediate up to</span>
                              <v-menu offset-y left>
                                <template #activator="{ on, attrs }">
                                  <v-btn icon small v-bind="attrs" v-on="on">
                                    <v-icon small color="primary">mdi-information-outline</v-icon>
                                  </v-btn>
                                </template>
                                <v-card max-width="320">
                                  <v-card-text class="py-2 px-3 caption">
                                    Counts above rare and up to this value &rarr; intermediate.
                                    Counts above this value are habitual and discarded.
                                  </v-card-text>
                                </v-card>
                              </v-menu>
                            </template>
                          </v-text-field>
                        </v-col>
                      </v-row>
                      <div class="iac-band-bar mt-2">
                        <div
                          class="iac-band-bar__seg iac-band-bar__seg--rare"
                          :style="{ flexGrow: bandsPreview.rareWeight }"
                        >
                          <span class="iac-band-bar__label">Rare</span>
                          <span class="iac-band-bar__range">0–{{ bandsPreview.rare }}</span>
                        </div>
                        <div
                          class="iac-band-bar__seg iac-band-bar__seg--inter"
                          :style="{ flexGrow: bandsPreview.interWeight }"
                        >
                          <span class="iac-band-bar__label">Intermediate</span>
                          <span class="iac-band-bar__range"
                            >{{ bandsPreview.rare + 1 }}–{{ bandsPreview.inter }}</span
                          >
                        </div>
                        <div
                          class="iac-band-bar__seg iac-band-bar__seg--freq"
                          :style="{ flexGrow: bandsPreview.freqWeight }"
                        >
                          <span class="iac-band-bar__label">Habitual</span>
                          <span class="iac-band-bar__range">{{ bandsPreview.inter + 1 }}+</span>
                        </div>
                      </div>
                      <p class="caption grey--text mb-0 mt-2">
                        Splits the per-video gesture count into rare / intermediate / habitual
                        bands.
                      </p>
                    </v-card-text>
                  </v-card>
                </section>

                <!-- ===================== COHERENCE ===================== -->
                <section v-if="featureFlags.coherence" v-show="activeSection === 'coherence'" class="iac-section">
                  <header class="iac-section__head">
                    <h2 class="iac-section__title">Coherence</h2>
                    <p class="iac-section__subtitle">
                      How we cross-check gestures against what the subject says.
                    </p>
                  </header>

                  <v-card outlined class="iac-subcard">
                    <v-card-title class="sub-engine-card-title"
                      >Matching &amp; detection</v-card-title
                    >
                    <v-card-text class="sub-engine-card-body">
                      <IacSliderField
                        v-for="f in semanticMatchingFields"
                        :key="f.key"
                        :label="f.label"
                        :help="f.help"
                        :min="f.min"
                        :max="f.max"
                        :step="f.step"
                        :unit="f.unit"
                        :decimals="f.decimals"
                        :value="form.analysisEngines[f.engine][f.key]"
                        @input="setField(f, $event)"
                      />
                      <IacSliderField
                        label="Idea detection threshold"
                        help="Idea coherence below this marks a detection candidate."
                        :min="0"
                        :max="100"
                        :step="1"
                        unit="%"
                        :decimals="0"
                        :value="form.analysisEngines.coherence.ideaCoincidenceThresholdPct"
                        @input="
                          setField(
                            { engine: 'coherence', key: 'ideaCoincidenceThresholdPct' },
                            $event
                          )
                        "
                      />
                    </v-card-text>
                  </v-card>

                  <v-card outlined class="iac-subcard">
                    <v-expansion-panels v-model="rulesetPanel" flat>
                      <v-expansion-panel>
                        <v-expansion-panel-header class="iac-rules-header">
                          <div class="d-flex flex-wrap align-center" style="gap: 8px; width: 100%">
                            <span class="sub-engine-card-title pa-0">Contradiction rules</span>
                            <v-chip x-small outlined>{{ coherenceRules.length }}</v-chip>
                            <v-spacer />
                            <v-btn
                              small
                              outlined
                              color="primary"
                              type="button"
                              class="mr-2"
                              @click.stop="resetCoherenceRulesetToBuiltinDefaults"
                            >
                              Reset parameters to defaults
                            </v-btn>
                          </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                          <template v-if="form.analysisEngines.coherence.ruleset">
                            <div class="d-flex align-center flex-wrap mb-3" style="gap: 8px">
                              <v-select
                                :value="form.analysisEngines.coherence.rulesetVersion"
                                :items="rulesetVersionItems"
                                label="Catalog"
                                dense
                                hide-details
                                outlined
                                style="max-width: 320px"
                                @change="onRulesetVersionChange"
                              />
                              <v-text-field
                                v-model="rulesetSearch"
                                prepend-inner-icon="mdi-magnify"
                                placeholder="Search rule…"
                                dense
                                hide-details
                                outlined
                                clearable
                                class="ruleset-search"
                                style="max-width: 280px"
                              />
                              <v-btn-toggle v-model="rulesetFilter" mandatory dense>
                                <v-btn small value="all"
                                  >All ({{
                                    form.analysisEngines.coherence.ruleset.rules.length
                                  }})</v-btn
                                >
                                <v-btn small value="weighted"
                                  >Weighted ({{ weightedRulesCount }})</v-btn
                                >
                                <v-btn small value="unweighted"
                                  >No weight ({{ unweightedRulesCount }})</v-btn
                                >
                              </v-btn-toggle>
                              <v-spacer />
                              <v-switch
                                v-model="rulesetShowAll"
                                dense
                                hide-details
                                class="mt-0 pt-0"
                                label="Show all"
                              />
                            </div>

                            <v-row dense>
                              <v-col
                                v-for="rule in paginatedRuleset"
                                :key="'rule-card-' + rule.ruleId"
                                cols="12"
                                sm="6"
                                md="4"
                                lg="3"
                              >
                                <v-card
                                  outlined
                                  class="rule-card"
                                  :class="{ 'rule-card--unweighted': Number(rule.reduction) === 0 }"
                                >
                                  <v-card-text class="pa-3">
                                    <div class="d-flex align-center" style="gap: 6px">
                                      <div
                                        class="subtitle-2 font-weight-medium text-truncate flex-grow-1"
                                        :title="rule.ruleId"
                                      >
                                        {{ rule.ruleId }}
                                      </div>
                                      <v-chip
                                        v-if="!rule.semanticIntents || !rule.semanticIntents.length"
                                        x-small
                                        color="warning"
                                        text-color="white"
                                        class="rule-card__unfired-chip"
                                      >
                                        <v-icon x-small left>mdi-alert-outline</v-icon>
                                        Won't fire
                                      </v-chip>
                                    </div>
                                    <div
                                      class="caption grey--text text--darken-1 text-truncate"
                                      :title="rule.ruleId"
                                    >
                                      {{ rule.ruleId }}
                                    </div>

                                    <div class="mt-3">
                                      <div
                                        class="d-flex justify-space-between caption grey--text mb-1"
                                      >
                                        <span>Weight</span>
                                        <span class="font-weight-medium"
                                          >−{{
                                            Math.round((Number(rule.reduction) || 0) * 100)
                                          }}%</span
                                        >
                                      </div>
                                      <v-slider
                                        :value="Number(rule.reduction) || 0"
                                        min="0"
                                        max="1"
                                        step="0.05"
                                        hide-details
                                        dense
                                        :color="reductionChipColor(rule.reduction)"
                                        track-color="grey lighten-3"
                                        @input="rule.reduction = $event"
                                      />
                                    </div>

                                    <div class="mt-3">
                                      <div class="caption grey--text mb-1 d-flex align-center">
                                        <v-icon x-small class="mr-1">mdi-link-variant</v-icon>
                                        Triggers when subject says…
                                      </div>
                                      <v-combobox
                                        v-model="rule.semanticIntents"
                                        multiple
                                        small-chips
                                        deletable-chips
                                        clearable
                                        dense
                                        outlined
                                        hide-details
                                        placeholder="Add + Enter"
                                        class="rule-card__intents"
                                      />
                                    </div>

                                    <div class="rule-card__quick-row mt-3">
                                      <v-select
                                        :value="speakingPreconditionKey(rule)"
                                        :items="speakingPreconditionItems"
                                        item-text="text"
                                        item-value="value"
                                        dense
                                        outlined
                                        hide-details
                                        label="Speaking"
                                        @change="setSpeakingPreconditionKey(rule, $event)"
                                      />
                                      <v-text-field
                                        v-model.number="rule.windowSeconds"
                                        type="number"
                                        step="0.5"
                                        min="0.5"
                                        max="120"
                                        dense
                                        outlined
                                        hide-details
                                        label="Window (s)"
                                      />
                                    </div>

                                    <v-expansion-panels flat tile class="rule-card__advanced mt-2">
                                      <v-expansion-panel>
                                        <v-expansion-panel-header
                                          class="px-0 py-1 caption grey--text"
                                        >
                                          Advanced
                                        </v-expansion-panel-header>
                                        <v-expansion-panel-content class="px-0">
                                          <v-text-field
                                            :value="rule.sourceItem"
                                            dense
                                            outlined
                                            hide-details
                                            disabled
                                            label="Source item"
                                            class="mb-2"
                                          />
                                          <v-text-field
                                            :value="rule.sourceValue"
                                            dense
                                            outlined
                                            hide-details
                                            disabled
                                            label="Source value"
                                          />
                                        </v-expansion-panel-content>
                                      </v-expansion-panel>
                                    </v-expansion-panels>
                                  </v-card-text>
                                </v-card>
                              </v-col>
                            </v-row>

                            <v-card v-if="!filteredRuleset.length" flat class="pa-6 text-center">
                              <v-icon size="36" color="grey">mdi-magnify-close</v-icon>
                              <p class="mt-2 mb-0 grey--text text--darken-1">
                                No rules match the current filter.
                              </p>
                            </v-card>

                            <v-row
                              v-if="!rulesetShowAll && rulesetTotalPages > 1"
                              class="mt-3"
                              justify="center"
                            >
                              <v-pagination
                                v-model="rulesetPage"
                                :length="rulesetTotalPages"
                                total-visible="7"
                              />
                            </v-row>
                          </template>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </v-card>
                </section>

                <!-- ===================== SCORING ===================== -->
                <section v-if="featureFlags.coherence" v-show="activeSection === 'scoring'" class="iac-section">
                  <header class="iac-section__head">
                    <h2 class="iac-section__title">Scoring &amp; results</h2>
                    <p class="iac-section__subtitle">
                      How the final verdict is presented to reviewers.
                    </p>
                  </header>

                  <v-card outlined class="iac-subcard">
                    <v-card-title class="sub-engine-card-title">Coherence semaphore</v-card-title>
                    <v-card-text class="sub-engine-card-body">
                      <v-row>
                        <v-col cols="12" sm="6">
                          <v-text-field
                            v-model.number="form.analysisEngines.coherence.semaphoreThresholdGood"
                            type="number"
                            dense
                          >
                            <template #label>
                              <span>Green above</span>
                              <v-menu offset-y left>
                                <template #activator="{ on, attrs }">
                                  <v-btn icon small v-bind="attrs" v-on="on">
                                    <v-icon small color="primary">mdi-information-outline</v-icon>
                                  </v-btn>
                                </template>
                                <v-card max-width="320">
                                  <v-card-text class="py-2 px-3 caption">
                                    Minimum score required for the green coherence band in UI
                                    indicators.
                                  </v-card-text>
                                </v-card>
                              </v-menu>
                            </template>
                          </v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6">
                          <v-text-field
                            v-model.number="form.analysisEngines.coherence.semaphoreThresholdWarn"
                            type="number"
                            dense
                          >
                            <template #label>
                              <span>Amber above</span>
                              <v-menu offset-y left>
                                <template #activator="{ on, attrs }">
                                  <v-btn icon small v-bind="attrs" v-on="on">
                                    <v-icon small color="primary">mdi-information-outline</v-icon>
                                  </v-btn>
                                </template>
                                <v-card max-width="320">
                                  <v-card-text class="py-2 px-3 caption">
                                    Minimum score required for the amber band; values below this
                                    threshold are shown as red.
                                  </v-card-text>
                                </v-card>
                              </v-menu>
                            </template>
                          </v-text-field>
                        </v-col>
                      </v-row>
                      <div class="iac-semaphore-bar mt-2">
                        <div
                          class="iac-semaphore-bar__seg iac-semaphore-bar__seg--red"
                          :style="{ flexGrow: semaphorePreview.red }"
                        >
                          <span class="iac-semaphore-bar__label">Red</span>
                          <span class="iac-semaphore-bar__range"
                            >0–{{ semaphorePreview.warn }}</span
                          >
                        </div>
                        <div
                          class="iac-semaphore-bar__seg iac-semaphore-bar__seg--amber"
                          :style="{ flexGrow: semaphorePreview.amber }"
                        >
                          <span class="iac-semaphore-bar__label">Amber</span>
                          <span class="iac-semaphore-bar__range"
                            >{{ semaphorePreview.warn }}–{{ semaphorePreview.good }}</span
                          >
                        </div>
                        <div
                          class="iac-semaphore-bar__seg iac-semaphore-bar__seg--green"
                          :style="{ flexGrow: semaphorePreview.green }"
                        >
                          <span class="iac-semaphore-bar__label">Green</span>
                          <span class="iac-semaphore-bar__range"
                            >{{ semaphorePreview.good }}–100</span
                          >
                        </div>
                      </div>
                      <p class="caption grey--text mb-0 mt-2">
                        Coherence scores map to a traffic light: green is trustworthy, red needs
                        review.
                      </p>
                    </v-card-text>
                  </v-card>
                </section>

                <!-- ===================== ADVANCED ===================== -->
                <section v-show="activeSection === 'advanced'" class="iac-section">
                  <header class="iac-section__head">
                    <h2 class="iac-section__title">Advanced</h2>
                    <p class="iac-section__subtitle">
                      Technical performance settings. The defaults work for most cases.
                    </p>
                  </header>

                  <v-card outlined class="iac-subcard">
                    <v-card-title class="sub-engine-card-title">Frame processing</v-card-title>
                    <v-card-text class="sub-engine-card-body">
                      <IacSliderField
                        v-for="f in advancedProcessingFields"
                        :key="f.key"
                        :label="f.label"
                        :help="f.help"
                        :min="f.min"
                        :max="f.max"
                        :step="f.step"
                        :unit="f.unit"
                        :decimals="f.decimals"
                        :value="form.analysisEngines[f.engine][f.key]"
                        @input="setField(f, $event)"
                      />
                    </v-card-text>
                  </v-card>

                  <v-card outlined class="iac-subcard">
                    <v-card-title class="sub-engine-card-title"
                      >Model inference (SageMaker)</v-card-title
                    >
                    <v-card-text class="sub-engine-card-body">
                      <IacSliderField
                        v-for="f in advancedInferenceFields"
                        :key="f.key"
                        :label="f.label"
                        :help="f.help"
                        :min="f.min"
                        :max="f.max"
                        :step="f.step"
                        :unit="f.unit"
                        :decimals="f.decimals"
                        :value="form.analysisEngines[f.engine][f.key]"
                        @input="setField(f, $event)"
                      />
                      <v-row>
                        <v-col cols="12" sm="6">
                          <v-select
                            v-model="form.analysisEngines.gestures.mlFramePersistence"
                            :items="mlFramePersistenceItems"
                            item-text="text"
                            item-value="value"
                            dense
                          >
                            <template #label>
                              <span>Stored model output per frame</span>
                              <CoherenceFieldHelp
                                text="How much of what the model returns for every analyzed frame is stored. Labels only keeps the predicted class and gaze; probabilities adds the full per-class distribution; everything also adds the raw logits, which the probabilities alone cannot reconstruct. Off stores nothing."
                              />
                            </template>
                          </v-select>
                        </v-col>
                      </v-row>
                      <div class="d-flex align-center">
                        <v-switch
                          v-model="form.analysisEngines.gestures.mlEpisodeSummary"
                          dense
                          hide-details
                          class="mt-0 pt-0"
                          label="Store model summary on episodes"
                        />
                        <CoherenceFieldHelp
                          text="Attach a fixed-size summary of the model output (per-class means, predicted-label histogram, confidence margin) to every episode, plus a run-level rollup. Turning it off reproduces the previous episode payload exactly."
                        />
                      </div>
                      <div class="d-flex align-center mt-2">
                        <v-switch
                          v-model="form.analysisEngines.gestures.mlAlertDetail"
                          dense
                          hide-details
                          class="mt-0 pt-0"
                          label="Show model detail on alerts"
                        />
                        <CoherenceFieldHelp
                          text="Expose the model summary as readable values on each alert and on its coherence row. Requires the episode summary above; turning it off reproduces the previous alert payload exactly."
                        />
                      </div>
                    </v-card-text>
                  </v-card>

                  <v-card outlined class="iac-subcard">
                    <v-card-title class="sub-engine-card-title">Transcription</v-card-title>
                    <v-card-text class="sub-engine-card-body">
                      <v-row>
                        <v-col cols="12" sm="6">
                          <v-select
                            v-model="form.analysisEngines.coherence.transcriptionProvider"
                            :items="transcriptionProviderItems"
                            item-text="text"
                            item-value="value"
                            dense
                          >
                            <template #label>
                              <span>Transcription provider</span>
                              <v-menu offset-y left>
                                <template #activator="{ on, attrs }">
                                  <v-btn icon small v-bind="attrs" v-on="on">
                                    <v-icon small color="primary">mdi-information-outline</v-icon>
                                  </v-btn>
                                </template>
                                <v-card max-width="320">
                                  <v-card-text class="py-2 px-3 caption">
                                    Speech-to-text service used to transcribe the interview audio
                                    (diarization and word-level timestamps).
                                  </v-card-text>
                                </v-card>
                              </v-menu>
                            </template>
                          </v-select>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </section>

                <!-- ===================== VERSIONS ===================== -->
                <section v-show="activeSection === 'versions'" class="iac-section iac-versions">
                  <header class="iac-section__head">
                    <h2 class="iac-section__title">Versions</h2>
                    <p class="iac-section__subtitle">
                      Every save that changes the engines creates a new version; runs seal the
                      version they used.
                    </p>
                  </header>
                  <v-data-table
                    :headers="versionHeaders"
                    :items="configVersions"
                    :loading="loadingConfigVersions"
                    item-key="id"
                    dense
                    disable-sort
                    hide-default-footer
                    :items-per-page="-1"
                    class="iac-versions__table"
                    no-data-text="No versions stored yet"
                  >
                    <template #[`item.version`]="{ item }">
                      <v-chip x-small outlined color="primary">v{{ item.version }}</v-chip>
                    </template>
                    <template #[`item.created_at`]="{ item }">
                      {{ item.created_at ? formatVersionDate(item.created_at) : '—' }}
                    </template>
                    <template #[`item.created_by`]="{ item }">
                      {{ formatVersionAuthor(item.created_by) }}
                    </template>
                    <template #[`item.notes`]="{ item }">
                      {{ item.notes || '—' }}
                    </template>
                    <template #[`item.used_by_runs`]="{ item }">
                      {{ item.used_by_runs }} {{ item.used_by_runs === 1 ? 'run' : 'runs' }}
                    </template>
                    <template #[`item.edited_in_place`]="{ item }">
                      <v-chip v-if="item.edited_in_place" x-small color="warning" outlined>
                        Edited in place
                      </v-chip>
                      <span v-else class="grey--text">—</span>
                    </template>
                  </v-data-table>
                </section>
              </div>
            </div>
          </v-card-text>
          <v-card-actions class="pa-4 interview-config-dialog-actions flex-shrink-0">
            <v-text-field
              v-model="saveNote"
              label="Version note"
              placeholder="What changed in this version"
              dense
              outlined
              hide-details
              maxlength="500"
              class="iac-save-note"
            />
            <v-spacer />
            <v-btn text @click="close">Cancel</v-btn>
            <v-btn type="submit" class="ok-modal" :disabled="saving">
              <v-progress-circular v-if="saving" indeterminate size="18" width="2" class="mr-2" />
              {{ editing ? 'Update' : 'Create' }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import defaultCoherenceRulesetV1 from '@/assets/default-veracity-ruleset.v1.json';
import defaultCoherenceRulesetV2 from '@/assets/default-veracity-ruleset.v2.json';

/**
 * Built-in coherence catalogs, mirroring BUILTIN_COHERENCE_RULESETS in ai-service.
 * v2 is a strict superset of v1: it adds the gestures the business Excel never
 * modelled, all at reduction 0, so picking it changes no score until one is raised.
 */
const VERACITY_RULESET_VERSIONS = Object.freeze({
  v1: defaultCoherenceRulesetV1,
  v2: defaultCoherenceRulesetV2,
});
const DEFAULT_VERACITY_RULESET_VERSION = 'v1';
import InterviewsDataService from '@/services/InterviewsDataService';
import featureFlags from '@/config/featureFlags';
import { currentGestureName } from '@/utils/gestureLabels';

/** Inline help for coherence rule card fields (English; admin UI). */
const COHERENCE_RULE_FIELD_HELP = {
  ruleId:
    'Stable key that must match how gesture alerts are bound to this rule in the analysis engine. If it does not match, the rule will never run.',
  ruleLabel:
    'Human-readable title for this rule (reports, review UI, expansion panel header). You can edit it; the catalog default is only the initial value.',
  sourceItem:
    'Catalog field from the gesture model this rule is tied to (e.g. brows). Keep aligned with production gesture metadata.',
  sourceValue:
    'Catalog value for that field (e.g. brows_down). Together with source item, this identifies the gesture signal.',
  reduction:
    'Fraction of coherence to subtract (0–1) from the idea the gesture appears in, when preconditions pass and the semantic classifier finds a match in that idea.',
  windowSeconds:
    'Seconds on each side of the gesture time, always inside the idea the gesture appears in: the classifier reads the words of that idea within [t − N, t + N], cut at the edges of that idea. Example: N = 5 → up to 5s before and 5s after the gesture, never a word of a neighbouring idea. A gesture in a pause between ideas is raised without semantics and never subtracts.',
  speakingPrecondition:
    'Optional gate before calling the classifier: require speech, require silence, or no audio constraint at the gesture time.',
  semanticIncompatibilities:
    'Short descriptions of verbal semantics that would contradict this gesture in context. They are sent to the LLM with the transcript snippet; the model judges whether that speech aligns with any of these lines (not literal keyword matching). Use spaces between words; avoid underscores.',
};

/** Human titles + render order for gesture catalog categories. */
const GESTURE_CATEGORY_TITLES = {
  illustrations: 'Illustrations',
  manipulations: 'Manipulations',
  speech_features: 'Speech features',
  emblems: 'Emblems',
  gesture_expressions: 'Facial expressions',
};
const GESTURE_CATEGORY_ORDER = [
  'illustrations',
  'manipulations',
  'speech_features',
  'emblems',
  'gesture_expressions',
];

/**
 * engines.coherence.segmentStrategy is retired: ideas are the only segmentation and a
 * gesture counts only in the idea it appears in. A stored value is rewritten to it.
 */
const SEGMENT_STRATEGY = 'idea';

/** Allowed values for engines.gestures.mlFramePersistence (backend enum). */
const ML_FRAME_PERSISTENCE_VALUES = ['off', 'labels', 'probabilities', 'full'];

const toIntOr = (value, fallback) => {
  if (value === null || value === undefined || value === '') return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n) : fallback;
};

const defaultForm = () => ({
  id: null,
  name: '',
  description: '',
  analysisEngines: {
    gestures: {
      gestureThresholdMs: 500,
      aboveBaselineMultiplier: 1.5,
      belowBaselineMultiplier: 0.5,
      baselineWindowSeconds: 30,
      targetFps: 10,
      sagemakerBatchSize: 12,
      sagemakerConcurrency: 8,
      sampledFramesJpegQuality: 85,
      gestureCatalog: [],
      absoluteBands: {
        rareMaxCount: 2,
        intermediateMaxCount: 5,
      },
      mlConfidenceThreshold: 0,
      mlFramePersistence: 'full',
      mlEpisodeSummary: true,
      mlAlertDetail: true,
    },
    coherence: {
      semanticWindowSeconds: 5,
      bulkSize: 12,
      semaphoreThresholdGood: 80,
      semaphoreThresholdWarn: 50,
      ideaCoincidenceThresholdPct: 50,
      reductionByRule: {},
      transcriptionProvider: 'elevenlabs',
      rulesetVersion: 'v1',
      ruleset: null,
    },
  },
});

const CoherenceFieldHelp = {
  props: {
    text: { type: String, required: true },
  },
  render(h) {
    const text = this.text;
    return h(
      'v-menu',
      {
        props: {
          offsetY: true,
          left: true,
          maxWidth: 320,
          closeOnContentClick: true,
        },
        scopedSlots: {
          activator: ({ on, attrs }) =>
            h(
              'v-btn',
              {
                props: { icon: true, xSmall: true, type: 'button' },
                attrs: { ...attrs, 'aria-label': 'Field help' },
                on,
                class: 'flex-shrink-0 ml-1 field-help-btn',
              },
              [h('v-icon', { props: { small: true, color: 'primary' } }, 'mdi-information-outline')]
            ),
        },
      },
      [
        h('v-card', { props: { maxWidth: 320 } }, [
          h('v-card-text', { staticClass: 'py-2 px-3 caption' }, text),
        ]),
      ]
    );
  },
};

/** Labelled slider row with optional inline help and a formatted value readout. */
const IacSliderField = {
  components: { CoherenceFieldHelp },
  props: {
    label: { type: String, required: true },
    help: { type: String, default: '' },
    value: { type: [Number, String], default: 0 },
    min: { type: Number, default: 0 },
    max: { type: Number, default: 100 },
    step: { type: Number, default: 1 },
    unit: { type: String, default: '' },
    decimals: { type: Number, default: 0 },
  },
  computed: {
    displayValue() {
      const n = Number(this.value);
      const v = Number.isFinite(n) ? n.toFixed(this.decimals) : (0).toFixed(this.decimals);
      if (!this.unit) return v;
      const tight = this.unit === '×' || this.unit === '%';
      return tight ? `${v}${this.unit}` : `${v} ${this.unit}`;
    },
  },
  render(h) {
    const labelChildren = [h('span', this.label)];
    if (this.help) {
      labelChildren.push(h(CoherenceFieldHelp, { props: { text: this.help } }));
    }
    return h('div', { class: 'iac-slider-row' }, [
      h('div', { class: 'iac-slider-row__label' }, labelChildren),
      h('v-slider', {
        class: 'iac-slider-row__slider',
        props: {
          value: Number(this.value),
          min: this.min,
          max: this.max,
          step: this.step,
          hideDetails: true,
          dense: true,
          color: 'primary',
          trackColor: 'grey lighten-3',
        },
        on: { input: (val) => this.$emit('input', val) },
      }),
      h('div', { class: 'iac-slider-row__value' }, this.displayValue),
    ]);
  },
};

export default {
  name: 'InterviewAnalysisTypesList',
  components: {
    IacSliderField,
    CoherenceFieldHelp,
  },
  data() {
    return {
      configs: [],
      headers: [
        { text: 'Name', value: 'name', sortable: false },
        { text: 'Version', value: 'configVersion', sortable: false },
        { text: 'Created at', value: 'createdAt', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
      versionHeaders: [
        { text: 'Version', value: 'version', sortable: false },
        { text: 'Date', value: 'created_at', sortable: false },
        { text: 'Author', value: 'created_by', sortable: false },
        { text: 'Note', value: 'notes', sortable: false },
        { text: 'Used by', value: 'used_by_runs', sortable: false },
        { text: 'Edited in place', value: 'edited_in_place', sortable: false },
      ],
      configVersions: [],
      loadingConfigVersions: false,
      saveNote: '',
      loading: false,
      saving: false,
      success: false,
      message: '',
      dialog: false,
      editing: false,
      form: defaultForm(),
      rules: [(v) => !!v || 'Required field'],
      pagination: {
        page: 1,
        itemsPerPage: 10,
        totalPages: 1,
        totalItems: 0,
      },
      coherenceRulesPanel: [],
      speakingPreconditionItems: [
        { value: 'any', text: 'Any' },
        { value: 'speaking', text: 'Speaking required' },
        { value: 'silent', text: 'Silence required' },
      ],
      // Counts come from the assets so the label cannot drift from the catalog.
      rulesetVersionItems: Object.entries(VERACITY_RULESET_VERSIONS).map(([key, rs]) => ({
        value: key,
        text: `coherence_ruleset_${key} (${rs.rules.length} Rules)`,
      })),
      mlFramePersistenceItems: [
        { value: 'off', text: 'Off' },
        { value: 'labels', text: 'Labels only' },
        { value: 'probabilities', text: 'Labels and probabilities' },
        { value: 'full', text: 'Everything (adds raw logits)' },
      ],
      // OpenAI is still accepted by the engine but no longer offered here.
      transcriptionProviderItems: [{ value: 'elevenlabs', text: 'ElevenLabs (Scribe)' }],
      coherenceRuleFieldHelp: COHERENCE_RULE_FIELD_HELP,
      rulesetSearch: '',
      rulesetFilter: 'all', // 'all' | 'weighted' | 'unweighted'
      rulesetPage: 1,
      rulesetItemsPerPage: 12,
      rulesetShowAll: false,
      // Contradiction rules expansion panel: collapsed by default (null index).
      rulesetPanel: null,
      // Gesture management: catalog (fetched) is kept separate from per-gesture
      // settings (map keyed by gestureName: { method, applySustainedRule, discardWhileSpeaking, enabled }).
      gestureCatalog: [],
      gestureSettings: {},
      gestureCatalogLoading: false,
      gestureCatalogError: false,
      gestureSearch: '',
      // Left-nav reorganization: which section is currently shown.
      activeSection: 'general',
      // Polished slider controls. Each row binds to form.analysisEngines[engine][key].
      gestureSensitivityFields: [
        {
          engine: 'gestures',
          key: 'gestureThresholdMs',
          label: 'Minimum gesture duration',
          help: 'Minimum duration to classify an event as a sustained gesture; lower values increase sensitivity.',
          min: 100,
          max: 5000,
          step: 50,
          unit: 'ms',
          decimals: 0,
        },
        {
          engine: 'gestures',
          key: 'baselineWindowSeconds',
          label: 'Baseline window',
          help: 'Time range used to estimate baseline behavior; larger windows are more stable but less reactive.',
          min: 1,
          max: 300,
          step: 1,
          unit: 's',
          decimals: 0,
        },
        {
          engine: 'gestures',
          key: 'aboveBaselineMultiplier',
          label: 'Alert when above baseline',
          help: 'Factor used to trigger alerts when gesture frequency rises above baseline.',
          min: 0.1,
          max: 10,
          step: 0.1,
          unit: '×',
          decimals: 1,
        },
        {
          engine: 'gestures',
          key: 'belowBaselineMultiplier',
          label: 'Alert when below baseline',
          help: 'Factor used to trigger alerts when gesture frequency drops below baseline.',
          min: 0.01,
          max: 2,
          step: 0.01,
          unit: '×',
          decimals: 2,
        },
      ],
      semanticMatchingFields: [
        {
          engine: 'coherence',
          key: 'semanticWindowSeconds',
          label: 'Context window',
          help: 'Default for rules without their own window: the classifier reads the words of the idea the gesture appears in that fall within this many seconds on each side of the gesture, never words of a neighbouring idea.',
          min: 1,
          max: 60,
          step: 1,
          unit: 's',
          decimals: 0,
        },
        {
          engine: 'coherence',
          key: 'bulkSize',
          label: 'Processing batch size',
          help: 'Number of semantic classification items processed per batch request.',
          min: 1,
          max: 128,
          step: 1,
          unit: '',
          decimals: 0,
        },
      ],
      advancedProcessingFields: [
        {
          engine: 'gestures',
          key: 'targetFps',
          label: 'Frame sampling rate',
          help: 'Frame sampling rate for gesture analysis; higher values catch shorter gestures but take longer.',
          min: 1,
          max: 30,
          step: 1,
          unit: 'fps',
          decimals: 0,
        },
        {
          engine: 'gestures',
          key: 'sampledFramesJpegQuality',
          label: 'Frame image quality',
          help: 'JPEG quality (30–100) for frames written to disk during sampling; higher is sharper and larger.',
          min: 30,
          max: 100,
          step: 1,
          unit: '%',
          decimals: 0,
        },
      ],
      advancedInferenceFields: [
        {
          engine: 'gestures',
          key: 'sagemakerBatchSize',
          label: 'Inference batch size',
          help: 'Number of frames sent per inference batch; larger batches improve throughput but increase memory usage.',
          min: 1,
          max: 128,
          step: 1,
          unit: '',
          decimals: 0,
        },
        {
          engine: 'gestures',
          key: 'sagemakerConcurrency',
          label: 'Parallel inference requests',
          help: 'Maximum parallel inference requests; higher values can reduce latency but may saturate resources.',
          min: 1,
          max: 64,
          step: 1,
          unit: '',
          decimals: 0,
        },
        {
          engine: 'gestures',
          key: 'mlConfidenceThreshold',
          label: 'Minimum model confidence',
          help: 'Minimum winning-class probability for a facial submodel prediction to activate its gesture. 0 disables the gate and keeps every argmax prediction.',
          min: 0,
          max: 1,
          step: 0.01,
          unit: '',
          decimals: 2,
        },
      ],
    };
  },

  computed: {
    coherenceRules() {
      return this.form?.analysisEngines?.coherence?.ruleset?.rules || [];
    },
    weightedRulesCount() {
      return this.coherenceRules.filter((r) => Number(r.reduction) > 0).length;
    },
    unweightedRulesCount() {
      return this.coherenceRules.filter((r) => Number(r.reduction) === 0).length;
    },
    filteredRuleset() {
      const q = String(this.rulesetSearch || '')
        .trim()
        .toLowerCase();
      return this.coherenceRules.filter((r) => {
        if (this.rulesetFilter === 'weighted' && !(Number(r.reduction) > 0)) return false;
        if (this.rulesetFilter === 'unweighted' && !(Number(r.reduction) === 0)) return false;
        if (!q) return true;
        const haystack = [
          r.ruleId,
          r.ruleLabel,
          r.sourceItem,
          r.sourceValue,
          ...(Array.isArray(r.semanticIntents) ? r.semanticIntents : []),
        ]
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      });
    },
    rulesetTotalPages() {
      if (this.rulesetShowAll) return 1;
      const per = Math.max(1, Number(this.rulesetItemsPerPage) || 12);
      return Math.max(1, Math.ceil(this.filteredRuleset.length / per));
    },
    paginatedRuleset() {
      if (this.rulesetShowAll) return this.filteredRuleset;
      const per = Math.max(1, Number(this.rulesetItemsPerPage) || 12);
      const start = Math.max(0, (this.rulesetPage - 1) * per);
      return this.filteredRuleset.slice(start, start + per);
    },
    filteredGestureGroups() {
      const q = String(this.gestureSearch || '')
        .trim()
        .toLowerCase();
      const matches = this.gestureCatalog.filter((g) => {
        if (!q) return true;
        return [g.documentLabelEn, g.gestureName].join(' ').toLowerCase().includes(q);
      });
      const byCategory = new Map();
      matches.forEach((g) => {
        const category = g.category || 'other';
        if (!byCategory.has(category)) byCategory.set(category, []);
        byCategory.get(category).push(g);
      });
      const orderedCategories = [
        ...GESTURE_CATEGORY_ORDER.filter((c) => byCategory.has(c)),
        ...[...byCategory.keys()].filter((c) => !GESTURE_CATEGORY_ORDER.includes(c)),
      ];
      return orderedCategories.map((category) => ({
        category,
        title:
          GESTURE_CATEGORY_TITLES[category] ||
          category.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()),
        items: byCategory.get(category),
      }));
    },
    enabledGestureCount() {
      return this.gestureCatalog.filter((g) => {
        const s = this.gestureSettings[g.gestureName];
        return !s || s.enabled !== false;
      }).length;
    },
    // Gestures the engine detects that can never reach a coherence rule: they show
    // up in the gestures panel but never affect the coherence score. The
    // binding status comes from the ai-service catalog (single source of truth).
    unmappedGestures() {
      return this.gestureCatalog.filter((g) => this.gestureBindingStatus(g) === 'unbound');
    },
    partiallyMappedGestures() {
      return this.gestureCatalog.filter((g) => this.gestureBindingStatus(g) === 'partial');
    },
    rareMaxCountRules() {
      return [
        (v) =>
          (v !== '' && v !== null && Number.isInteger(Number(v)) && Number(v) >= 0) ||
          'Integer of 0 or more required',
      ];
    },
    intermediateMaxCountRules() {
      const rare = Number(this.form?.analysisEngines?.gestures?.absoluteBands?.rareMaxCount);
      return [
        (v) =>
          (v !== '' && v !== null && Number.isInteger(Number(v)) && Number(v) >= 1) ||
          'Integer of 1 or more required',
        (v) =>
          !Number.isFinite(rare) || Number(v) > rare || 'Must be greater than rare band max count',
      ];
    },
    ideaThresholdRules() {
      return [
        (v) =>
          (v !== '' &&
            v !== null &&
            Number.isInteger(Number(v)) &&
            Number(v) >= 0 &&
            Number(v) <= 100) ||
          'Integer between 0 and 100 required',
      ];
    },
    featureFlags() {
      return featureFlags;
    },
    navSections() {
      const catalogCount = this.gestureCatalog.length;
      const rulesCount = this.coherenceRules.length;
      const sections = [
        { key: 'general', icon: 'mdi-file-document-outline', label: 'General', badge: '' },
        {
          key: 'gestures',
          icon: 'mdi-hand-back-right-outline',
          label: 'Gestures',
          badge: catalogCount ? `${this.enabledGestureCount}/${catalogCount}` : '',
        },
        {
          key: 'coherence',
          icon: 'mdi-shield-check-outline',
          label: 'Coherence',
          badge: rulesCount ? String(rulesCount) : '',
        },
        { key: 'scoring', icon: 'mdi-traffic-light', label: 'Scoring & results', badge: '' },
        { key: 'advanced', icon: 'mdi-tune-variant', label: 'Advanced', badge: '' },
      ];
      if (this.editing) {
        sections.push({
          key: 'versions',
          icon: 'mdi-history',
          label: 'Versions',
          badge: this.configVersions.length ? String(this.configVersions.length) : '',
        });
      }
      // Coherence-only sections (Coherence + Scoring/semaphore) are hidden
      // when the coherence feature flag is off (CDTI demo scope).
      if (featureFlags.coherence) return sections;
      const coherenceSectionKeys = ['coherence', 'scoring'];
      return sections.filter((s) => !coherenceSectionKeys.includes(s.key));
    },
    /** Latest stored version of the config being edited (versions list, else the active doc). */
    currentConfigVersion() {
      const latest = this.configVersions[0];
      if (latest && Number.isFinite(Number(latest.version))) return Number(latest.version);
      const active = Number(this.form?.activeGesturesConfig?.version);
      return Number.isFinite(active) && active > 0 ? active : null;
    },
    semaphorePreview() {
      const clamp = (n) => Math.max(0, Math.min(100, Number(n) || 0));
      const good = clamp(this.form.analysisEngines.coherence.semaphoreThresholdGood);
      const warnRaw = clamp(this.form.analysisEngines.coherence.semaphoreThresholdWarn);
      const warn = Math.min(warnRaw, good);
      return {
        good,
        warn,
        red: warn,
        amber: Math.max(0, good - warn),
        green: Math.max(0, 100 - good),
      };
    },
    bandsPreview() {
      const bands = this.form.analysisEngines.gestures.absoluteBands || {};
      const rare = Math.max(0, toIntOr(bands.rareMaxCount, 2));
      const inter = Math.max(rare + 1, toIntOr(bands.intermediateMaxCount, 5));
      return {
        rare,
        inter,
        rareWeight: rare + 1,
        interWeight: Math.max(1, inter - rare),
        freqWeight: Math.max(2, Math.ceil((inter + 1) / 2)),
      };
    },
  },

  watch: {
    rulesetSearch() {
      this.rulesetPage = 1;
    },
    rulesetFilter() {
      this.rulesetPage = 1;
    },
    rulesetShowAll() {
      this.rulesetPage = 1;
    },
    rulesetTotalPages(next) {
      if (this.rulesetPage > next) this.rulesetPage = next;
    },
  },
  async mounted() {
    await this.load(1);
  },
  methods: {
    async load(page) {
      this.loading = true;
      try {
        const response = await InterviewsDataService.findAllInterviewAnalysisTypes(
          page,
          this.pagination.itemsPerPage
        );
        if (response.status === 200) {
          this.configs = response.data.data || [];
          this.pagination.page = page;
          this.pagination.totalPages = response.data.links?.totalPages || 1;
          this.pagination.totalItems =
            response.data.links?.totalItems ||
            this.pagination.totalPages * this.pagination.itemsPerPage;
        }
      } catch (error) {
        this.success = false;
        this.message = 'Error loading interview analysis configs.';
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    handlePageChange(newPage) {
      this.pagination.page = newPage;
      this.load(newPage);
    },
    async openCreate() {
      this.form = defaultForm();
      this.coherenceRulesPanel = [];
      this.editing = false;
      this.saveNote = '';
      this.configVersions = [];
      this.activeSection = 'general';
      this.ensureCanonicalCoherenceRuleset();
      this.dialog = true;
      try {
        const response = await InterviewsDataService.getInterviewAnalysisTypeDefaults();
        const engines = response?.data?.data;
        if (engines && typeof engines === 'object') {
          this.form.analysisEngines = {
            ...defaultForm().analysisEngines,
            ...engines,
            gestures: {
              ...defaultForm().analysisEngines.gestures,
              ...(engines.gestures || {}),
            },
            coherence: {
              ...defaultForm().analysisEngines.coherence,
              ...(engines.coherence || {}),
            },
          };
          this.ensureCanonicalCoherenceRuleset();
        }
      } catch (error) {
        // Fallback to local defaults when no Mongo defaults are available.
        console.warn('Using local defaults for interview analysis config', error);
      }
      this.normalizeGestureEngineExtras();
      await this.loadGestureManagement(this.form.analysisEngines.gestures.gestureCatalog);
    },
    async openEdit(item) {
      this.editing = true;
      this.dialog = true;
      this.saving = true;
      this.activeSection = 'general';
      this.saveNote = '';
      this.loadConfigVersions(item.id);
      try {
        const response = await InterviewsDataService.findOneInterviewAnalysisType(item.id);
        const fullItem = response?.data?.data || item;
        this.form = {
          ...defaultForm(),
          ...fullItem,
          analysisEngines: {
            ...defaultForm().analysisEngines,
            ...(fullItem.activeGesturesConfig?.engines || {}),
            gestures: {
              ...defaultForm().analysisEngines.gestures,
              ...(fullItem.activeGesturesConfig?.engines?.gestures || {}),
            },
            coherence: {
              ...defaultForm().analysisEngines.coherence,
              ...(fullItem.activeGesturesConfig?.engines?.coherence || {}),
            },
          },
        };
      } catch (error) {
        this.success = false;
        this.message = 'Error loading interview analysis config detail.';
        this.form = {
          ...defaultForm(),
          ...item,
        };
        console.error(error);
      } finally {
        this.saving = false;
      }
      this.ensureCanonicalCoherenceRuleset();
      this.normalizeGestureEngineExtras();
      await this.loadGestureManagement(this.form.analysisEngines.gestures.gestureCatalog);
    },
    async loadConfigVersions(id) {
      this.configVersions = [];
      if (!id) return;
      this.loadingConfigVersions = true;
      try {
        const response = await InterviewsDataService.getConfigVersions(id);
        const items = response?.data?.data;
        this.configVersions = Array.isArray(items) ? items : [];
      } catch (error) {
        this.configVersions = [];
        console.error(error);
      } finally {
        this.loadingConfigVersions = false;
      }
    },
    configVersionOf(item) {
      const raw =
        item?.configVersion ?? item?.config_version ?? item?.activeGesturesConfig?.version;
      const version = Number(raw);
      return Number.isFinite(version) && version > 0 ? version : null;
    },
    formatVersionAuthor(createdBy) {
      return createdBy !== null && createdBy !== undefined ? `User #${createdBy}` : '—';
    },
    formatVersionDate(value) {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
    },
    close() {
      this.dialog = false;
      this.form = defaultForm();
      this.coherenceRulesPanel = [];
      this.editing = false;
      this.saveNote = '';
      this.configVersions = [];
      this.gestureSearch = '';
      this.activeSection = 'general';
      this.rulesetPanel = null;
    },
    formatSemanticIncompatibilityPhrase(s) {
      return String(s || '')
        .replace(/_/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    },
    cloneBuiltinCoherenceRuleset(version) {
      const key = this.normalizeRulesetVersion(
        version === undefined ? this.form?.analysisEngines?.coherence?.rulesetVersion : version
      );
      return JSON.parse(JSON.stringify(VERACITY_RULESET_VERSIONS[key]));
    },
    normalizeRulesetVersion(version) {
      const key = String(version || '').trim().toLowerCase();
      return VERACITY_RULESET_VERSIONS[key] ? key : DEFAULT_VERACITY_RULESET_VERSION;
    },
    onRulesetVersionChange(version) {
      // Switching catalog replaces the rules, so per-rule edits are lost. The
      // reductions of the rules both versions share are carried over, since
      // those are the tuning the analyst is most likely to care about.
      const key = this.normalizeRulesetVersion(version);
      const previous = this.form.analysisEngines.coherence.ruleset;
      const next = this.cloneBuiltinCoherenceRuleset(key);
      if (previous && Array.isArray(previous.rules)) {
        const bySavedId = new Map(previous.rules.map((r) => [String(r.ruleId || '').trim(), r]));
        next.rules.forEach((rule) => {
          const saved = bySavedId.get(String(rule.ruleId || '').trim());
          if (!saved) return;
          if (Number.isFinite(Number(saved.reduction))) rule.reduction = Number(saved.reduction);
          if (Array.isArray(saved.semanticIntents)) rule.semanticIntents = [...saved.semanticIntents];
          if (Number.isFinite(Number(saved.windowSeconds))) rule.windowSeconds = Number(saved.windowSeconds);
          if (saved.requiresIsSpeaking !== undefined) rule.requiresIsSpeaking = saved.requiresIsSpeaking;
        });
      }
      this.$set(this.form.analysisEngines.coherence, 'rulesetVersion', key);
      this.$set(this.form.analysisEngines.coherence, 'ruleset', next);
      this.rulesetPage = 1;
    },
    mergeSavedRulesOntoCanonical(saved) {
      const base = this.cloneBuiltinCoherenceRuleset();
      if (!saved || typeof saved !== 'object' || !Array.isArray(saved.rules)) {
        return base;
      }
      const byId = new Map(
        saved.rules
          .filter((r) => r && String(r.ruleId || '').trim())
          .map((r) => [String(r.ruleId).trim(), r])
      );
      base.rules = base.rules.map((canonicalRow) => {
        const s = byId.get(canonicalRow.ruleId);
        if (!s) {
          return { ...canonicalRow };
        }
        const intents = Array.isArray(s.semanticIntents)
          ? s.semanticIntents
              .map((x) => this.formatSemanticIncompatibilityPhrase(String(x)))
              .filter(Boolean)
          : [...canonicalRow.semanticIntents];
        const red = Number(s.reduction);
        const ws = Number(s.windowSeconds);
        const label =
          typeof s.ruleLabel === 'string' && String(s.ruleLabel).trim()
            ? String(s.ruleLabel).trim()
            : canonicalRow.ruleLabel;
        return {
          ...canonicalRow,
          ruleLabel: label,
          reduction: Number.isFinite(red) ? red : canonicalRow.reduction,
          windowSeconds: Number.isFinite(ws) ? ws : canonicalRow.windowSeconds,
          semanticIntents: intents.length ? intents : [...canonicalRow.semanticIntents],
          requiresIsSpeaking:
            s.requiresIsSpeaking === true ? true : s.requiresIsSpeaking === false ? false : null,
        };
      });
      return base;
    },
    ensureCanonicalCoherenceRuleset() {
      const v = this.form.analysisEngines.coherence;
      const merged = this.mergeSavedRulesOntoCanonical(v.ruleset);
      this.$set(v, 'ruleset', merged);
      this.normalizeCoherenceRulesetAfterLoad();
    },
    normalizeCoherenceRulesetAfterLoad() {
      const rs = this.form.analysisEngines.coherence.ruleset;
      if (!rs || typeof rs !== 'object' || !Array.isArray(rs.rules)) return;
      rs.rules.forEach((rule, i) => {
        if (!Array.isArray(rule.semanticIntents)) {
          this.$set(rs.rules[i], 'semanticIntents', []);
        } else {
          this.$set(
            rs.rules[i],
            'semanticIntents',
            rule.semanticIntents
              .map((x) => this.formatSemanticIncompatibilityPhrase(String(x)))
              .filter(Boolean)
          );
        }
      });
    },
    resetCoherenceRulesetToBuiltinDefaults() {
      this.$set(this.form.analysisEngines.coherence, 'ruleset', this.cloneBuiltinCoherenceRuleset());
      this.$nextTick(() => {
        this.normalizeCoherenceRulesetAfterLoad();
        this.coherenceRulesPanel = [];
      });
    },
    speakingPreconditionKey(rule) {
      if (rule.requiresIsSpeaking === true) return 'speaking';
      if (rule.requiresIsSpeaking === false) return 'silent';
      return 'any';
    },
    reductionChipColor(value) {
      const n = Number(value) || 0;
      if (n >= 0.6) return 'red darken-2';
      if (n >= 0.3) return 'orange darken-2';
      if (n > 0) return 'amber darken-2';
      return 'grey';
    },
    setSpeakingPreconditionKey(rule, key) {
      if (key === 'speaking') this.$set(rule, 'requiresIsSpeaking', true);
      else if (key === 'silent') this.$set(rule, 'requiresIsSpeaking', false);
      else this.$set(rule, 'requiresIsSpeaking', null);
    },
    validateCoherenceRulesetForSave() {
      const rs = this.form.analysisEngines.coherence.ruleset;
      const version = this.normalizeRulesetVersion(
        this.form.analysisEngines.coherence.rulesetVersion
      );
      const expected = VERACITY_RULESET_VERSIONS[version].rules.map((r) => r.ruleId);
      if (!rs || !Array.isArray(rs.rules)) {
        this.success = false;
        this.message = 'Coherence ruleset is missing. Reload the dialog.';
        return false;
      }
      const actual = rs.rules.map((r) => String(r.ruleId || '').trim());
      if (actual.length !== expected.length || expected.some((id, i) => id !== actual[i])) {
        this.success = false;
        this.message =
          'Coherence ruleset no longer matches the fixed catalog. Close and reopen this config, or reset parameters to defaults.';
        return false;
      }
      return true;
    },
    normalizeGestureEngineExtras() {
      const g = this.form.analysisEngines.gestures;
      const bands = g.absoluteBands && typeof g.absoluteBands === 'object' ? g.absoluteBands : {};
      this.$set(g, 'absoluteBands', {
        rareMaxCount: Math.max(0, toIntOr(bands.rareMaxCount, 2)),
        intermediateMaxCount: Math.max(1, toIntOr(bands.intermediateMaxCount, 5)),
      });
      if (!Array.isArray(g.gestureCatalog)) {
        this.$set(g, 'gestureCatalog', []);
      }
      // A config version saved before these knobs existed arrives without
      // them: fill in the same defaults every other layer uses.
      const threshold = Number(g.mlConfidenceThreshold);
      this.$set(
        g,
        'mlConfidenceThreshold',
        Number.isFinite(threshold) ? Math.max(0, Math.min(1, threshold)) : 0
      );
      if (!ML_FRAME_PERSISTENCE_VALUES.includes(g.mlFramePersistence)) {
        this.$set(g, 'mlFramePersistence', 'full');
      }
      this.$set(g, 'mlEpisodeSummary', g.mlEpisodeSummary !== false);
      this.$set(g, 'mlAlertDetail', g.mlAlertDetail !== false);
      const v = this.form.analysisEngines.coherence;
      const pct = toIntOr(v.ideaCoincidenceThresholdPct, toIntOr(v.detectionThresholdPct, 50));
      this.$set(v, 'ideaCoincidenceThresholdPct', Math.max(0, Math.min(100, pct)));
      if (v.segmentStrategy !== undefined) this.$set(v, 'segmentStrategy', SEGMENT_STRATEGY);
      if (v.singleIdeaAttribution !== undefined) this.$set(v, 'singleIdeaAttribution', true);
      // Stored configs written before catalog versions existed have none; they
      // were scored with v1, so that is what they must keep showing.
      this.$set(v, 'rulesetVersion', this.normalizeRulesetVersion(v.rulesetVersion));
    },
    async fetchGestureCatalog() {
      this.gestureCatalogLoading = true;
      this.gestureCatalogError = false;
      try {
        const response = await InterviewsDataService.getGestureCatalog();
        const body = response?.data?.data || response?.data || {};
        this.gestureCatalog = Array.isArray(body.gestures) ? body.gestures : [];
        if (!this.gestureCatalog.length) {
          this.gestureCatalogError = true;
        }
      } catch (error) {
        this.gestureCatalog = [];
        this.gestureCatalogError = true;
        console.error(error);
      } finally {
        this.gestureCatalogLoading = false;
      }
    },
    async loadGestureManagement(storedCatalog) {
      await this.fetchGestureCatalog();
      this.initGestureSettings(storedCatalog);
    },
    defaultGestureSetting(catalogItem) {
      return {
        method: catalogItem && catalogItem.defaultMethod === 'baseline' ? 'baseline' : 'absolute',
        applySustainedRule: !catalogItem || catalogItem.defaultApplySustainedRule !== false,
        discardWhileSpeaking: !!(catalogItem && catalogItem.defaultDiscardWhileSpeaking),
        enabled: true,
      };
    },
    initGestureSettings(storedCatalog) {
      // Configs saved before a gesture was renamed hold the old id; resolve it
      // so the analyst's choices survive instead of reverting to defaults.
      const stored = new Map(
        (Array.isArray(storedCatalog) ? storedCatalog : [])
          .filter((r) => r && String(r.gestureName || '').trim())
          .map((r) => [currentGestureName(String(r.gestureName).trim()), r])
      );
      const map = {};
      this.gestureCatalog.forEach((item) => {
        const base = this.defaultGestureSetting(item);
        const s = stored.get(item.gestureName);
        map[item.gestureName] = s
          ? {
              method: s.method === 'baseline' || s.method === 'absolute' ? s.method : base.method,
              applySustainedRule:
                typeof s.applySustainedRule === 'boolean'
                  ? s.applySustainedRule
                  : base.applySustainedRule,
              discardWhileSpeaking:
                typeof s.discardWhileSpeaking === 'boolean'
                  ? s.discardWhileSpeaking
                  : base.discardWhileSpeaking,
              enabled: typeof s.enabled === 'boolean' ? s.enabled : base.enabled,
            }
          : base;
      });
      this.gestureSettings = map;
    },
    resetGestureSettingsToDefaults() {
      this.initGestureSettings(null);
    },
    // --- Coherence binding hints -------------------------------------------
    // 'bound' is the normal case and shows no marker; the rest get an icon so
    // the analyst sees, before launching an analysis, which gestures are
    // detected but never scored. An older ai-service without coherenceBinding
    // degrades to 'bound' (no marker) instead of crying wolf on every row.
    gestureBindingStatus(item) {
      return String(item?.coherenceBinding?.status || 'bound');
    },
    gestureBindingDetail(item) {
      const binding = item?.coherenceBinding;
      if (!binding) return '';
      const rules = Array.isArray(binding.ruleIds) ? binding.ruleIds : [];
      const detail = String(binding.detail || '');
      if (this.gestureBindingStatus(item) === 'partial' && rules.length) {
        return `${detail} Rules it can reach: ${rules.join(', ')}.`;
      }
      return detail;
    },
    gestureBindingIcon(item) {
      const map = {
        unbound: 'mdi-alert-outline',
        partial: 'mdi-alert-circle-outline',
      };
      return map[this.gestureBindingStatus(item)] || '';
    },
    gestureBindingColor(item) {
      return this.gestureBindingStatus(item) === 'unbound' ? 'warning' : 'grey';
    },
    setCategoryMethod(category, method) {
      this.gestureCatalog.forEach((item) => {
        if ((item.category || 'other') !== category) return;
        const s = this.gestureSettings[item.gestureName];
        if (s) s.method = method;
      });
    },
    setField(field, value) {
      const engine = this.form.analysisEngines[field.engine];
      if (engine) this.$set(engine, field.key, value);
    },
    buildAnalysisEnginesPayload() {
      const analysisEngines = JSON.parse(JSON.stringify(this.form.analysisEngines));
      const v = analysisEngines.coherence;
      delete v.semanticClassifier;
      delete v.semanticModel;
      const canonical = this.cloneBuiltinCoherenceRuleset();
      const byId = Object.fromEntries(
        (v.ruleset && Array.isArray(v.ruleset.rules) ? v.ruleset.rules : [])
          .filter((r) => r && String(r.ruleId || '').trim())
          .map((r) => [String(r.ruleId).trim(), r])
      );
      v.ruleset = {
        version: canonical.version,
        rules: canonical.rules.map((c) => {
          const r = byId[c.ruleId] || c;
          return {
            ruleId: c.ruleId,
            ruleLabel:
              typeof r.ruleLabel === 'string' && String(r.ruleLabel).trim()
                ? String(r.ruleLabel).trim()
                : c.ruleLabel,
            sourceItem: c.sourceItem,
            sourceValue: c.sourceValue,
            reduction: Math.max(0, Math.min(1, Number(r.reduction) || 0)),
            windowSeconds: Math.min(120, Math.max(0.5, Number(r.windowSeconds) || 5)),
            semanticIntents: (Array.isArray(r.semanticIntents) ? r.semanticIntents : [])
              .map((x) => this.formatSemanticIncompatibilityPhrase(String(x)))
              .filter(Boolean),
            requiresIsSpeaking:
              r.requiresIsSpeaking === true ? true : r.requiresIsSpeaking === false ? false : null,
          };
        }),
      };
      const g = analysisEngines.gestures;
      if (this.gestureCatalog.length) {
        // One entry per catalog gesture, always the full catalog.
        g.gestureCatalog = this.gestureCatalog.map((item) => {
          const s = this.gestureSettings[item.gestureName] || this.defaultGestureSetting(item);
          return {
            gestureName: item.gestureName,
            method: s.method === 'baseline' ? 'baseline' : 'absolute',
            applySustainedRule: s.applySustainedRule === true,
            discardWhileSpeaking: s.discardWhileSpeaking === true,
            enabled: s.enabled !== false,
          };
        });
      }
      const bands = g.absoluteBands && typeof g.absoluteBands === 'object' ? g.absoluteBands : {};
      g.absoluteBands = {
        rareMaxCount: Math.max(0, toIntOr(bands.rareMaxCount, 2)),
        intermediateMaxCount: Math.max(1, toIntOr(bands.intermediateMaxCount, 5)),
      };
      const mlThreshold = Number(g.mlConfidenceThreshold);
      g.mlConfidenceThreshold = Number.isFinite(mlThreshold)
        ? Math.max(0, Math.min(1, mlThreshold))
        : 0;
      if (!ML_FRAME_PERSISTENCE_VALUES.includes(g.mlFramePersistence)) {
        g.mlFramePersistence = 'full';
      }
      g.mlEpisodeSummary = g.mlEpisodeSummary !== false;
      g.mlAlertDetail = g.mlAlertDetail !== false;
      const ideaPct = Math.max(0, Math.min(100, toIntOr(v.ideaCoincidenceThresholdPct, 50)));
      v.ideaCoincidenceThresholdPct = ideaPct;
      if (Object.prototype.hasOwnProperty.call(v, 'detectionThresholdPct')) {
        v.detectionThresholdPct = ideaPct;
      }
      if (v.segmentStrategy !== undefined) v.segmentStrategy = SEGMENT_STRATEGY;
      if (v.singleIdeaAttribution !== undefined) v.singleIdeaAttribution = true;
      v.rulesetVersion = this.normalizeRulesetVersion(v.rulesetVersion);
      return analysisEngines;
    },
    async save() {
      if (!this.validateCoherenceRulesetForSave()) return;
      if (!this.$refs.configForm.validate()) return;
      this.saving = true;
      try {
        const payload = {
          name: this.form.name,
          description: this.form.description,
          analysisEngines: this.buildAnalysisEnginesPayload(),
        };
        const note = (this.saveNote || '').trim();
        if (note) payload.note = note;
        const response = this.editing
          ? await InterviewsDataService.updateInterviewAnalysisType({
              ...payload,
              id: this.form.id,
            })
          : await InterviewsDataService.createInterviewAnalysisType(payload);
        if ([200, 201, 204].includes(response.status)) {
          this.success = true;
          const configVersion = response?.data?.data?.config_version;
          if (this.editing && configVersion && configVersion.version_created === false) {
            this.message = `No changes — version not created (still Config v${configVersion.version}).`;
          } else {
            this.message = this.editing
              ? 'Interview Analysis Config updated.'
              : 'Interview Analysis Config created.';
          }
          await this.load(1);
          this.close();
        }
      } catch (error) {
        this.success = false;
        this.message = 'Error saving interview analysis config.';
        console.error(error);
      } finally {
        this.saving = false;
      }
    },
    async remove(item) {
      try {
        await InterviewsDataService.deleteInterviewAnalysisType(item.id);
        this.success = true;
        this.message = 'Interview Analysis Config deleted.';
        await this.load(1);
      } catch (error) {
        this.success = false;
        this.message = 'Error deleting interview analysis config.';
        console.error(error);
      }
    },
  },
};
</script>

<style scoped>
.iac-save-note {
  max-width: 420px;
  margin-right: 12px;
}
.iac-versions__table {
  border: 1px solid rgba(0, 0, 0, 0.08);
}
.engine-card {
  height: auto;
  box-shadow: none !important;
}

.engine-card-title {
  min-height: auto;
  padding: 10px 12px 4px;
}

.engine-card-body {
  padding: 8px 12px 12px;
}

.sub-engine-card {
  margin-bottom: 12px;
  box-shadow: none !important;
}

.sub-engine-card-title {
  min-height: auto;
  padding: 8px 12px 4px;
  font-size: 14px;
}

.sub-engine-card-body {
  padding: 4px 12px 8px;
}

.engine-card-body >>> .v-col {
  padding-bottom: 0 !important;
}

.disabled-card {
  opacity: 0.55;
  pointer-events: none;
}

.interview-config-dialog-card {
  min-height: 100vh;
  height: 100%;
}

.interview-config-dialog-toolbar {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.interview-config-dialog-body {
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.interview-config-gestures-card .engine-card-body {
  max-width: 100%;
}

.gestures-analysis-flex {
  gap: 12px;
  width: 100%;
}

.gestures-analysis-flex-item {
  flex: 1 1 380px;
  min-width: min(100%, 260px);
  max-width: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.gestures-analysis-flex-item--full {
  flex: 1 1 100%;
  min-width: 100%;
}

.gestures-analysis-flex-item .sub-engine-card {
  flex: 1 1 auto;
  width: 100%;
  margin-bottom: 0 !important;
  min-height: 100%;
}

.coherence-rule-panels-wrap {
  max-height: min(calc(100vh - 220px), 72vh);
  overflow-y: auto;
  padding-right: 2px;
}

.coherence-rule-panels .v-expansion-panel {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  margin-bottom: 8px;
}

.coherence-rule-panels .v-expansion-panel:not(:last-child) {
  margin-bottom: 8px;
}

.coherence-rule-panel-header {
  min-height: 48px !important;
}

/* YouTube-style rule cards */
.rule-card {
  border-radius: 10px !important;
  background: #fff;
  display: flex;
  flex-direction: column;
  height: 100%;
  transition: box-shadow 0.15s ease;
}

.rule-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.rule-card--unweighted {
  background: #fafafa;
}

.rule-card__quick-row {
  display: grid;
  grid-template-columns: 1fr 110px;
  gap: 8px;
}

.rule-card__advanced ::v-deep .v-expansion-panel {
  box-shadow: none !important;
  background: transparent !important;
}

.rule-card__advanced ::v-deep .v-expansion-panel-content__wrap {
  padding: 4px 0 0 !important;
}

.rule-card__intents ::v-deep .v-input__slot {
  min-height: 40px;
}

.ruleset-search ::v-deep input {
  font-size: 13px;
}

/* Gesture management rows */
.gesture-search ::v-deep input {
  font-size: 13px;
}

.gesture-row {
  gap: 12px;
  padding: 6px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.gesture-row:last-child {
  border-bottom: none;
}

.gesture-row__label {
  min-width: 220px;
  flex: 1 1 220px;
}

.gesture-row__name {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 11px;
}

.gesture-row__controls {
  gap: 16px;
}

.field-help-btn {
  margin-top: -2px;
}

/* ===== Reorganized layout: left nav + content sections ===== */
.iac-layout {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding: 16px 24px 24px;
}

.iac-nav {
  flex: 0 0 220px;
  position: sticky;
  top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.iac-nav__group {
  font-size: 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.45);
  margin: 0 0 6px 10px;
}

.iac-nav__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 9px 12px;
  border: none;
  border-left: 3px solid transparent;
  border-radius: 6px;
  background: transparent;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.78);
  cursor: pointer;
  text-align: left;
  transition:
    background 0.12s ease,
    color 0.12s ease;
}

.iac-nav__item:hover {
  background: rgba(0, 0, 0, 0.04);
}

.iac-nav__item--active {
  background: rgba(0, 0, 0, 0.05);
  border-left-color: currentColor;
  font-weight: 500;
}

.iac-nav__item-main {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.iac-nav__item-icon {
  color: inherit !important;
}

.iac-nav__item-badge {
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 500;
  padding: 1px 8px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.06);
}

.iac-content {
  flex: 1 1 auto;
  min-width: 0;
}

.iac-section__head {
  margin-bottom: 16px;
}

.iac-section__title {
  font-size: 20px;
  font-weight: 500;
  margin: 0;
}

.iac-section__subtitle {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.55);
  margin: 2px 0 0;
}

.iac-subcard {
  margin-bottom: 16px;
  box-shadow: none !important;
  border-radius: 10px !important;
}

.gesture-category {
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  margin-bottom: 14px;
  overflow: hidden;
}

.gesture-category__head {
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.03);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.gesture-category__title {
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.7);
}

.gesture-category__body {
  padding: 2px 12px 6px;
}

.iac-rules-header {
  min-height: 52px;
}

.iac-rules-header .sub-engine-card-title {
  font-size: 14px;
}

.iac-group__title {
  font-size: 14px;
  font-weight: 500;
  margin: 0;
}

.iac-disabled-chip {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.5);
  box-shadow: none !important;
  border-radius: 8px !important;
  opacity: 0.7;
  pointer-events: none;
}

/* Slider rows (IacSliderField) */
.iac-slider-row {
  display: grid;
  grid-template-columns: minmax(150px, 220px) 1fr 76px;
  align-items: center;
  gap: 16px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.iac-slider-row:last-child {
  border-bottom: none;
}

.iac-slider-row__label {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.78);
}

.iac-slider-row__value {
  text-align: right;
  font-size: 14px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
}

/* Preview bars (bands + semaphore) */
.iac-band-bar,
.iac-semaphore-bar {
  display: flex;
  width: 100%;
  height: 44px;
  border-radius: 8px;
  overflow: hidden;
}

.iac-band-bar__seg,
.iac-semaphore-bar__seg {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 0;
  padding: 0 6px;
  text-align: center;
}

.iac-band-bar__label,
.iac-semaphore-bar__label {
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.iac-band-bar__range,
.iac-semaphore-bar__range {
  font-size: 11px;
  line-height: 1.2;
  opacity: 0.85;
}

.iac-band-bar__seg--freq,
.iac-semaphore-bar__seg--green {
  background: #e6f4ea;
  color: #1b5e20;
}

.iac-band-bar__seg--inter,
.iac-semaphore-bar__seg--amber {
  background: #fdf1dc;
  color: #8a5300;
}

.iac-band-bar__seg--rare,
.iac-semaphore-bar__seg--red {
  background: #fce8e6;
  color: #b3261e;
}

@media (max-width: 960px) {
  .iac-layout {
    flex-direction: column;
    gap: 12px;
    padding: 12px 16px 20px;
  }

  .iac-nav {
    position: static;
    flex: 1 1 auto;
    width: 100%;
    flex-direction: row;
    flex-wrap: wrap;
  }

  .iac-nav__group {
    width: 100%;
    margin-left: 4px;
  }

  .iac-nav__item {
    width: auto;
    border-left: none;
    border-bottom: 2px solid transparent;
    border-radius: 6px;
  }

  .iac-nav__item--active {
    border-left: none;
    border-bottom-color: currentColor;
  }

  .iac-slider-row {
    grid-template-columns: 1fr 64px;
    grid-template-areas:
      'label label'
      'slider value';
    gap: 6px 12px;
  }

  .iac-slider-row__label {
    grid-area: label;
  }

  .iac-slider-row__slider {
    grid-area: slider;
  }

  .iac-slider-row__value {
    grid-area: value;
  }
}
</style>

<style>
/* v-dialog fullscreen content is attached outside the component root; keep unscoped. */
.interview-config-dialog-content {
  margin: 0 !important;
  padding: 0 !important;
  height: 100%;
  max-height: 100vh;
  max-width: 100%;
}
</style>
